import { useQueries } from '@tanstack/react-query';
import { useAudienceCatalog } from '@/hooks/useAudienceCatalog';
import { useTranslation } from '@/hooks/useTranslation';
import { addressService } from '@/services/addressService';
import { CampaignAudience, nodePath } from '@/services/audienceService';
import { AdCampaignLocationTarget } from '@/types/ads';

export default function CampaignSegmentationSummary({ locations, audience, embedded = false }: {
  locations: AdCampaignLocationTarget[];
  audience: CampaignAudience;
  embedded?: boolean;
}) {
  const { t: { audience: copy } } = useTranslation();
  const catalog = useAudienceCatalog();
  const labels = useQueries({ queries: locations.map(location => ({
    queryKey: ['address', 'summary', location.countryId, location.adminAreaId, location.localityId],
    enabled: !location.label,
    queryFn: async () => {
      const parts = await Promise.all([
        addressService.getCountry(location.countryId),
        location.adminAreaId ? addressService.getAdminArea(location.adminAreaId) : undefined,
        location.localityId ? addressService.getLocality(location.localityId) : undefined,
      ]);
      return parts.filter(part => part !== undefined).map(part => part.name).join(' / ');
    },
    staleTime: 600000,
  })) });
  const locationLabels = (excluded: boolean) => locations.flatMap((location, index) => {
    if (Boolean(location.excluded) !== excluded) return [];
    return [location.label || labels[index].data || (labels[index].isError
      ? [location.countryId, location.adminAreaId, location.localityId].filter(Boolean).join(' / ')
      : copy.loading)];
  });
  const groupLabels = (ids: string[]) => ids.map(id =>
    catalog.data ? nodePath(id, catalog.data.nodes) : catalog.isError ? id : copy.loading);
  const sections = [
    { title: copy.includeLocations, values: locationLabels(false), empty: copy.allGeographicLocations },
    { title: copy.excludeLocations, values: locationLabels(true), empty: copy.noExclusions },
    { title: copy.includeGroups, values: groupLabels(audience.includeNodeIds ?? []), empty: copy.allServiceGroups },
    { title: copy.excludeGroups, values: groupLabels(audience.excludeNodeIds ?? []), empty: copy.noExclusions },
  ];
  return <section className={embedded ? "space-y-3" : "space-y-3 border border-gray-200 p-3"} aria-label={copy.segmentation}>
    {!embedded && <h3 className="font-semibold text-gray-900">{copy.segmentation}</h3>}
    {sections.map(section => <div key={section.title}>
      <p className="mb-1 text-xs font-medium text-gray-500">{section.title}</p>
      {section.values.length ? <ul className="space-y-1">
        {section.values.map((label, index) => <li key={index} className="break-words rounded bg-gray-50 px-2 py-1 text-xs text-gray-700">{label}</li>)}
      </ul> : <p className="text-xs text-gray-700">{section.empty}</p>}
    </div>)}
    <p className="text-xs text-gray-500">{copy.rulesHelp}</p>
  </section>;
}
